import React from "react";
import Button from "@ui/Button";
import Checkbox from "@ui/Checkbox";
import Text from "@ui/Text";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import { SectionContainer } from "@ui/Section";
import TableCard from "@ui/Table/Card";
import { useProjects } from "../../../api/useProjects";
import { usePost } from "../../../api/useCall";
import { userMessage } from "../../../api/error";
import { styled } from "@linaria/react";
import { FlaskFill } from "@ui/IconV2/react";
import { color } from "@ui";
import { Navigate } from "react-router";
import { toPath } from "../../../router/helpers/path";
import { useCanUserDo } from "../../canUserDo";

import t from "../../../i18n";

type CacOperation = "launch" | "dispatch" | "reset";
type OperationStatus = "idle" | "waiting" | "running" | "success" | "failed";
type ProjectOperationState = { status: OperationStatus; error: string | null };

const MAX_CONCURRENT_DISPATCHES = 4;

const StyledLab = styled(FlaskFill)`
  display: flex;
  align-items: baseline;
  color: ${color.carbon800};
  height: 14px;
`;

const IconGroup = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const CacDashboard = () => {
  const canManageApplicationSettings = useCanUserDo("ADMIN_SETTING_MANAGE");
  const projects = useProjects();

  const launchCac = usePost("/projects/:projectId(uuid)/cac/launch", false);
  const dispatchCac = usePost("/projects/:projectId(uuid)/cac/dispatch", false);
  const resetCac = usePost("/projects/:projectId(uuid)/cac/reset", false);

  const [search, setSearch] = React.useState("");
  const [showProductionProjects, setShowProductionProjects] = React.useState(true);
  const [showResearchProjects, setShowResearchProjects] = React.useState(true);
  const [selectedProjectIds, setSelectedProjectIds] = React.useState<Set<string>>(() => new Set());
  const [operationStates, setOperationStates] = React.useState<
    Record<string, ProjectOperationState>
  >({});
  const [isRunning, setIsRunning] = React.useState(false);
  const [currentOperation, setCurrentOperation] = React.useState<CacOperation | null>(null);

  const visibleProjects = React.useMemo(() => {
    const q = search.trim().toLocaleLowerCase();
    return projects.filter(project => {
      if (project.draft && !showResearchProjects) return false;
      if (!project.draft && !showProductionProjects) return false;
      if (!q) return true;
      return project.name.toLocaleLowerCase().includes(q);
    });
  }, [projects, search, showProductionProjects, showResearchProjects]);

  const selectedCount = selectedProjectIds.size;
  const selectedVisibleCount = React.useMemo(
    () => visibleProjects.filter(project => selectedProjectIds.has(project.id)).length,
    [selectedProjectIds, visibleProjects],
  );
  const allSelected = visibleProjects.length > 0 && selectedVisibleCount === visibleProjects.length;
  const isPartialSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleProjects.length;

  React.useEffect(() => {
    const allowedIds = new Set(
      projects
        .filter(project => (project.draft ? showResearchProjects : showProductionProjects))
        .map(project => project.id),
    );

    setSelectedProjectIds(prev => {
      const next = new Set([...prev].filter(projectId => allowedIds.has(projectId)));
      return next.size === prev.size ? prev : next;
    });
    setOperationStates(prev => {
      const nextEntries = Object.entries(prev).filter(([projectId]) => allowedIds.has(projectId));
      return nextEntries.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(nextEntries);
    });
  }, [projects, showProductionProjects, showResearchProjects]);

  const toggleProject = React.useCallback((projectId: string, nextChecked: boolean) => {
    setSelectedProjectIds(prev => {
      const next = new Set(prev);
      if (nextChecked) next.add(projectId);
      else next.delete(projectId);
      return next;
    });
    setOperationStates(prev => {
      const current = prev[projectId];
      if (!current) return prev;
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }, []);

  const toggleSelectAll = React.useCallback(
    (nextChecked: boolean) => {
      setSelectedProjectIds(() => {
        if (!nextChecked) return new Set();
        return new Set(visibleProjects.map(project => project.id));
      });
      setOperationStates({});
    },
    [visibleProjects],
  );

  const globalStatus = React.useMemo(() => {
    const selectedIds = [...selectedProjectIds];
    if (!selectedIds.length) return null;

    let waiting = 0;
    let running = 0;
    let success = 0;
    let failed = 0;

    for (const id of selectedIds) {
      const state = operationStates[id]?.status ?? "idle";
      if (state === "waiting") waiting += 1;
      if (state === "running") running += 1;
      if (state === "success") success += 1;
      if (state === "failed") failed += 1;
    }

    return t("customization.section.cac.global_status", {
      running,
      failed,
      success,
      waiting,
    });
  }, [operationStates, selectedProjectIds]);

  const operationByType: Record<CacOperation, (projectId: string) => Promise<unknown>> =
    React.useMemo(
      () => ({
        launch: projectId => launchCac({ routeParams: { projectId } }),
        dispatch: projectId => dispatchCac({ routeParams: { projectId } }),
        reset: projectId => resetCac({ routeParams: { projectId } }),
      }),
      [dispatchCac, launchCac, resetCac],
    );

  const runSelected = React.useCallback(
    async (operation: CacOperation) => {
      if (isRunning) return;
      if (selectedProjectIds.size === 0) return;

      const request = operationByType[operation];
      setIsRunning(true);
      setCurrentOperation(operation);

      const selected = projects.filter(p => selectedProjectIds.has(p.id));
      setOperationStates(prev => {
        const next: Record<string, ProjectOperationState> = { ...prev };
        for (const project of selected) next[project.id] = { status: "waiting", error: null };
        return next;
      });

      let cursor = 0;
      let inFlight = 0;
      let done = 0;

      const getErrorContent = (error: unknown) => {
        const hasBody = error instanceof Error && Boolean(error.message);
        return hasBody ? error.message : userMessage(error);
      };

      await new Promise<void>(resolve => {
        const launchNext = () => {
          if (done === selected.length) {
            resolve();
            return;
          }

          while (inFlight < MAX_CONCURRENT_DISPATCHES && cursor < selected.length) {
            const project = selected[cursor]!;
            cursor += 1;
            inFlight += 1;

            setOperationStates(prev => ({
              ...prev,
              [project.id]: { status: "running", error: null },
            }));

            request(project.id)
              .then(() => {
                setOperationStates(prev => ({
                  ...prev,
                  [project.id]: { status: "success", error: null },
                }));
              })
              .catch(error => {
                setOperationStates(prev => ({
                  ...prev,
                  [project.id]: { status: "failed", error: getErrorContent(error) },
                }));
              })
              .finally(() => {
                inFlight -= 1;
                done += 1;
                launchNext();
              });
          }
        };

        launchNext();
      });

      setIsRunning(false);
      setCurrentOperation(null);
    },
    [isRunning, operationByType, projects, selectedProjectIds],
  );

  const isListDisabled = isRunning || (!showProductionProjects && !showResearchProjects);
  const operationLabelByType: Record<CacOperation, string> = React.useMemo(
    () => ({
      launch: t("customization.section.cac.launch.label"),
      dispatch: t("customization.section.cac.dispatch.label"),
      reset: t("customization.section.cac.reset.label"),
    }),
    [],
  );
  const runningOperationLabel =
    currentOperation === null ? "" : operationLabelByType[currentOperation];

  if (!canManageApplicationSettings)
    return <Navigate to={toPath("/administration/processes/monitoring", {})} replace />;

  return (
    <SectionContainer>
      <TableCard title={t("customization.section.cac.title")} headerLeft={null} headerRight={null}>
        <div className={container}>
          <div className={header}>
            <div className={actions}>
              <Button
                className={actionButton}
                variant="secondary"
                onClick={() => runSelected("launch")}
                disabled={isRunning || selectedCount === 0}
              >
                {t("customization.section.cac.launch.button", { count: selectedCount })}
              </Button>
              <Button
                className={actionButton}
                variant="primary"
                onClick={() => runSelected("dispatch")}
                disabled={isRunning || selectedCount === 0}
              >
                {t("customization.section.cac.dispatch.button", { count: selectedCount })}
              </Button>
              <Button
                className={actionButton}
                variant="destructive"
                onClick={() => runSelected("reset")}
                disabled={isRunning || selectedCount === 0}
              >
                {t("customization.section.cac.reset.button", { count: selectedCount })}
              </Button>
            </div>
            <Text type="body2" className={globalStatusText}>
              {globalStatus ?? t("customization.section.cac.no_operation_launched")}
            </Text>
          </div>

          <div className={filters}>
            <SearchInputV2
              aria-label={t("customization.section.cac.search.aria_label")}
              placeholder={t("customization.section.cac.search.placeholder")}
              value={search}
              onChange={e => setSearch(e.currentTarget.value)}
              disabled={isRunning}
              slim
            />
            <div className={selectionFilters}>
              <Checkbox
                checked={allSelected}
                partial={isPartialSelected}
                onChange={e => toggleSelectAll(e.currentTarget.checked)}
                disabled={isListDisabled || visibleProjects.length === 0}
              >
                {t("customization.section.cac.select_all", { total: visibleProjects.length })}
              </Checkbox>
              <Checkbox
                checked={showProductionProjects}
                onChange={e => setShowProductionProjects(e.currentTarget.checked)}
                disabled={isRunning}
              >
                {t("customization.section.cac.filter.show_production_projects")}
              </Checkbox>
              <Checkbox
                checked={showResearchProjects}
                onChange={e => setShowResearchProjects(e.currentTarget.checked)}
                disabled={isRunning}
              >
                {t("customization.section.cac.filter.show_research_projects")}
              </Checkbox>
            </div>
          </div>

          <div className={list} role="list">
            {visibleProjects.length === 0 ? (
              <Text type="body2" className={emptyState}>
                {t("customization.section.cac.empty_state")}
              </Text>
            ) : (
              visibleProjects.map(project => {
                const isSelected = selectedProjectIds.has(project.id);
                const state = operationStates[project.id] ?? { status: "idle", error: null };
                const disabled = isListDisabled;

                return (
                  <div key={project.id} role="listitem" className={row}>
                    <Checkbox
                      checked={isSelected}
                      onChange={e => toggleProject(project.id, e.currentTarget.checked)}
                      disabled={disabled}
                    >
                      <span className={rowLabel}>
                        <span className={projectName}>{project.name}</span>
                        {project.draft ? (
                          <IconGroup title={t("commons.tooltip.research")}>
                            <span>
                              <StyledLab />
                            </span>
                          </IconGroup>
                        ) : null}
                        <span className={projectStatus} data-status={state.status}>
                          {state.status === "idle" &&
                            (isSelected
                              ? t("customization.section.cac.project_status.selected")
                              : "")}
                          {state.status === "waiting" &&
                            t("customization.section.cac.project_status.waiting")}
                          {state.status === "running" &&
                            t("customization.section.cac.project_status.running")}
                          {state.status === "success" &&
                            t("customization.section.cac.project_status.success")}
                          {state.status === "failed" &&
                            t("customization.section.cac.project_status.failed")}
                        </span>
                        {state.error && <span className={errorText}>{state.error}</span>}
                      </span>
                    </Checkbox>
                  </div>
                );
              })
            )}
          </div>

          {isRunning && (
            <Text type="body2" className={runningHint}>
              {t("customization.section.cac.running_hint", {
                operation: runningOperationLabel,
              })}
            </Text>
          )}
        </div>
      </TableCard>
    </SectionContainer>
  );
};

export default CacDashboard;

const container = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
  padding-top: ${spacings.s};
`;

const header = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const actions = css`
  display: flex;
  gap: ${spacings.xs};
  flex-wrap: wrap;
`;

const actionButton = css`
  flex-grow: 1;
`;

const globalStatusText = css`
  color: ${colors.carbon700};
  text-align: center;
`;

const filters = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.s};
`;

const selectionFilters = css`
  display: flex;
  align-items: center;
  gap: ${spacings.m};
  flex-wrap: wrap;
`;

const list = css`
  border: 1px solid ${colors.titanium200};
  background-color: ${colors.hydrogen};
  border-radius: 8px;
  padding: ${spacings.s};
  max-height: 420px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};
`;

const row = css`
  padding: ${spacings.xxs} 0;
`;

const rowLabel = css`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const projectName = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const projectStatus = css`
  font-size: 12px;
  color: ${colors.carbon600};

  &[data-status="running"],
  &[data-status="waiting"] {
    color: ${colors.carbon700};
  }

  &[data-status="success"] {
    color: ${colors.success};
  }

  &[data-status="failed"] {
    color: ${colors.error};
  }
`;

const errorText = css`
  grid-column: 1 / -1;
  font-size: 12px;
  color: ${colors.error};
  margin-top: ${spacings.xxs};
  white-space: normal;
`;

const runningHint = css`
  color: ${colors.carbon700};
`;

const emptyState = css`
  color: ${colors.carbon600};
`;
